<div class="drawer lg:drawer-open">
  <input id="main-drawer" #drawerToggle type="checkbox" class="drawer-toggle"/>

  <!-- Main Content Area -->
  <div class="drawer-content flex flex-col">
    <!-- Top Header Bar -->
    <header class="bg-base-100 border-b border-base-300 h-14 flex items-center px-4 sticky top-0 z-50">
      <div class="flex-none lg:hidden">
        <label for="main-drawer" class="btn btn-ghost btn-sm btn-square" aria-label="Open navigation menu">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
          </svg>
        </label>
      </div>

      <div class="flex-1 flex items-center gap-2 lg:px-4">
        <fa-icon [icon]="faUtensils" class="text-lg"></fa-icon>
        <h1 class="text-base font-semibold">Pantry</h1>
      </div>

      <!-- Theme selector -->
      <div class="flex-none mr-2">
        <details #themeDropdown class="dropdown dropdown-end">
          <summary class="btn btn-ghost btn-sm btn-square" aria-label="Theme">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 18a6 6 0 1 1 0-12v12z"/>
              <path d="M12 2a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1zm0 18a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1zM2 12a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1zm18 0a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2h-1a1 1 0 0 1-1-1z"/>
            </svg>
          </summary>
          <ul class="menu menu-sm dropdown-content bg-base-100 rounded-box border border-base-300 mt-2 w-44 z-50">
            <li>
              <a (click)="onThemeModeChange('system'); closeDropdown(themeDropdown)">
                <span class="w-3">@if (themeMode === 'system') { ✓ }</span>
                <span>System</span>
              </a>
            </li>
            <li>
              <a (click)="onThemeModeChange('light'); closeDropdown(themeDropdown)">
                <span class="w-3">@if (themeMode === 'light') { ✓ }</span>
                <span>Light</span>
              </a>
            </li>
            <li>
              <a (click)="onThemeModeChange('dark'); closeDropdown(themeDropdown)">
                <span class="w-3">@if (themeMode === 'dark') { ✓ }</span>
                <span>Dark</span>
              </a>
            </li>
          </ul>
        </details>
      </div>

      @if (user) {
        @if (invites.length > 0) {
          <div class="flex-none mr-2 lg:hidden">
            <button
              type="button"
              class="btn btn-ghost btn-sm btn-square relative"
              aria-label="Toggle invitations"
              aria-controls="mobile-invitations-panel"
              [attr.aria-expanded]="mobileInvitationsExpanded"
              (click)="toggleMobileInvitations()"
            >
              <fa-icon [icon]="faInvitationIndicator"></fa-icon>
              <span class="badge badge-error badge-xs absolute -top-1 -right-1">{{ invites.length }}</span>
              <span class="sr-only">{{ invites.length }} pending invitations</span>
            </button>
          </div>

          <div class="flex-none mr-2 hidden lg:block">
            <app-invitations
              [invites]="invites"
              (acceptInvitationRequested)="acceptInvitation($event)"
              (rejectInvitationRequested)="rejectInvitation($event)"
            ></app-invitations>
          </div>
        }

        <div class="flex-none">
          <details #userDropdown class="dropdown dropdown-end">
            <summary class="btn btn-ghost btn-sm">{{ user.nickname }}</summary>
            <ul class="menu menu-sm dropdown-content bg-base-100 rounded-box border border-base-300 mt-2 w-40 z-50">
              <li><a (click)="logout(); closeDropdown(userDropdown)">Logout</a></li>
            </ul>
          </details>
        </div>
      }
    </header>

    @if (user && mobileInvitationsExpanded && invites.length > 0) {
      <section
        id="mobile-invitations-panel"
        class="mobile-invitations-shell border-b border-base-300 bg-base-100 px-4 pb-4 pt-0 lg:hidden"
      >
        <app-invitations
          [invites]="invites"
          [mode]="'mobile-inline'"
          (acceptInvitationRequested)="acceptInvitation($event)"
          (rejectInvitationRequested)="rejectInvitation($event)"
          (closeRequested)="closeMobileInvitations()"
        ></app-invitations>
      </section>
    }

    <!-- Page Content -->
    <main class="flex-1">
      @if (user) {
        <!-- Page Toolbar -->
        <div class="bg-base-100 border-b border-base-300 px-4 flex flex-col">
          <div class="flex flex-row items-center w-full min-h-16 py-2">
            <!-- Left: Back button + Page Title -->
            <div class="flex items-center gap-2 min-w-0">
              @if (backButton()) {
                <button
                  type="button"
                  class="btn btn-ghost btn-square"
                  (click)="onBackClick()"
                  [attr.aria-label]="backButton()?.label || 'Back'"
                >
                  <fa-icon [icon]="faArrowLeft"></fa-icon>
                </button>
              }
              @if (pageTitle()) {
                <h2 class="text-lg font-semibold truncate">{{ pageTitle() }}</h2>
              }
              @if (titleBadge(); as badge) {
                <span class="badge badge-info badge-sm gap-1 shrink-0" [title]="badge.tooltip || ''">
                  <span class="loading loading-spinner loading-xs"></span>
                  {{ badge.label }}
                </span>
              }
            </div>

            <!-- Center: Page Search (legacy template) -->
            @if (pageSearch()) {
              <div class="flex-1 lg:max-w-xl ms-2">
                <ng-container [ngTemplateOutlet]="pageSearch()"></ng-container>
              </div>
            }

            <!-- Right: declarative actions + legacy template fallback -->
            @if (hasAnyToolbarContent()) {
              <div class="ml-auto flex items-center gap-2 shrink-0">

                <!-- ===== Desktop (lg+) inline area ===== -->
                <div class="hidden lg:flex items-center gap-2">
                  <!-- Declarative: buttons -->
                  @for (a of desktopButtons(); track trackAction($index, a)) {
                    <ui-button
                      [variant]="variantFor(a)"
                      [tone]="toneFor(a)"
                      [disabled]="!!a.disabled"
                      [loading]="!!a.loading"
                      [attr.title]="a.tooltip || null"
                      (clicked)="runAction(a, $event)"
                    >
                      @if (a.icon) {
                        <fa-icon [icon]="a.icon"></fa-icon>
                      }
                      <span [class.ml-1]="!!a.icon">{{ a.label }}</span>
                    </ui-button>
                  }

                  <!-- Declarative: custom items -->
                  @for (c of desktopCustom(); track trackAction($index, c)) {
                    <ng-container [ngTemplateOutlet]="c.template"></ng-container>
                  }
                </div>

                <!-- ===== Mobile (<lg) inline area =====
                     Pinned buttons are NOT rendered here; they get a
                     dedicated full-width second row below the toolbar
                     (see further down). -->
                <div class="flex lg:hidden items-center gap-2">
                  <!-- Custom items: shown unless explicitly moved to submenu -->
                  @for (c of mobileCustomToolbar(); track trackAction($index, c)) {
                    <ng-container [ngTemplateOutlet]="c.template"></ng-container>
                  }

                </div>

                <!-- ===== Overflow ⋯ dropdown =====
                     Visible on mobile whenever we have collapsed buttons,
                     collapsed custom items, or legacy actions/menu templates.
                     Visible on desktop when there are items opted into
                     `alwaysInSubMenu` or when the legacy menuActions template
                     is set (those entries are intended for all viewports). -->
                @if (
                  mobileOverflowButtons().length > 0 ||
                  mobileCustomOverflow().length > 0 ||
                  alwaysSubMenuButtons().length > 0 ||
                  alwaysSubMenuCustom().length > 0 ||
                  pageMenuActionsTemplate() ||
                  pageActionsTemplate()
                ) {
                  <details
                    #overflowDetails
                    class="dropdown dropdown-end"
                    [ngClass]="{ 'lg:hidden': !hasDesktopOverflow() }"
                  >
                    <summary
                      type="button"
                      class="btn btn-ghost btn-square list-none"
                      aria-label="More actions"
                    >
                      <fa-icon [icon]="faEllipsisH"></fa-icon>
                    </summary>
                    <div
                      class="dropdown-content bg-base-100 rounded-box border border-base-300 z-50 w-72 p-2 flex flex-col gap-1"
                    >
                      <!-- All viewports: declarative buttons opted into `alwaysInSubMenu` -->
                      @if (alwaysSubMenuButtons().length > 0) {
                        <ul class="menu menu-md w-full p-0">
                          @for (a of alwaysSubMenuButtons(); track trackAction($index, a)) {
                            <li class="w-full">
                              <button
                                #alwaysItem
                                type="button"
                                [disabled]="!!a.disabled"
                                [class]="overflowItemClass(a)"
                                (click)="runAction(a, $event, alwaysItem)"
                              >
                                @if (a.icon) {
                                  <fa-icon [icon]="a.icon"></fa-icon>
                                }
                                <span>{{ a.label }}</span>
                              </button>
                            </li>
                          }
                        </ul>
                      }

                      <!-- All viewports: custom items opted into `alwaysInSubMenu` -->
                      @if (alwaysSubMenuCustom().length > 0) {
                        <div class="flex flex-col gap-1 *:w-full p-1">
                          @for (c of alwaysSubMenuCustom(); track trackAction($index, c)) {
                            <ng-container [ngTemplateOutlet]="c.template"></ng-container>
                          }
                        </div>
                      }

                      <!-- Divider after always-submenu block when more content follows below -->
                      @if (
                        (alwaysSubMenuButtons().length > 1 || alwaysSubMenuCustom().length > 1) &&
                        (mobileOverflowButtons().length > 1 ||
                          mobileCustomOverflow().length > 1 ||
                          pageActionsTemplate() ||
                          pageMenuActionsTemplate())
                      ) {
                        <div class="h-px bg-base-300 mx-2 my-1"></div>
                      }

                      <!-- Mobile-only: collapsed declarative buttons -->
                      @if (mobileOverflowButtons().length > 0) {
                        <ul class="menu menu-md w-full p-0 lg:hidden">
                          @for (a of mobileOverflowButtons(); track trackAction($index, a)) {
                            <li class="w-full">
                              <button
                                #overflowItem
                                type="button"
                                [disabled]="!!a.disabled"
                                [class]="overflowItemClass(a)"
                                (click)="runAction(a, $event, overflowItem)"
                              >
                                @if (a.icon) {
                                  <fa-icon [icon]="a.icon"></fa-icon>
                                }
                                <span>{{ a.label }}</span>
                              </button>
                            </li>
                          }
                        </ul>
                      }

                      <!-- Mobile-only: custom items moved to submenu -->
                      @if (mobileCustomOverflow().length > 0) {
                        <div class="lg:hidden flex flex-col gap-1 *:w-full p-1">
                          @for (c of mobileCustomOverflow(); track trackAction($index, c)) {
                            <ng-container [ngTemplateOutlet]="c.template"></ng-container>
                          }
                        </div>
                      }

                      <!-- Divider between mobile-collapsed area and shared menu actions -->
                      @if (
                        (mobileOverflowButtons().length > 1 ||
                          mobileCustomOverflow().length > 1 ||
                          pageActionsTemplate()) &&
                        pageMenuActionsTemplate()
                      ) {
                        <div class="lg:hidden h-px bg-base-300 mx-2 my-1"></div>
                      }

                    </div>
                  </details>
                }
              </div>
            }
          </div>

          <!-- Declarative: pinned buttons + pinned custom items
               → full-width second row on mobile.
               Each item takes an equal share of the available width. -->
          @if (mobileToolbarButtons().length > 0 || mobilePinnedCustom().length > 0) {
            <div class="flex lg:hidden gap-2 pb-3 items-stretch">
              @for (a of mobileToolbarButtons(); track trackAction($index, a)) {
                <ui-button
                  class="flex-1 min-w-0"
                  [variant]="variantFor(a)"
                  [tone]="toneFor(a)"
                  [disabled]="!!a.disabled"
                  [loading]="!!a.loading"
                  [fullWidth]="true"
                  [attr.title]="a.tooltip || null"
                  (clicked)="runAction(a, $event)"
                >
                  @if (a.icon) {
                    <fa-icon [icon]="a.icon"></fa-icon>
                  }
                  <span [class.ml-1]="!!a.icon">{{ a.label }}</span>
                </ui-button>
              }

              @for (c of mobilePinnedCustom(); track trackAction($index, c)) {
                <div class="flex-1 min-w-0 [&>*]:w-full">
                  <ng-container [ngTemplateOutlet]="c.template"></ng-container>
                </div>
              }
            </div>
          }
        </div>
      }
      <ng-content></ng-content>
    </main>
  </div>

  <!-- Sidebar -->
  <div class="drawer-side z-40 top-14 h-[calc(100vh-3.5rem)] lg:top-0 lg:h-screen">
    <label for="main-drawer" aria-label="Close navigation menu" class="drawer-overlay"></label>

    @if (user) {
      <nav class="bg-base-200 h-full w-64 flex flex-col pb-[calc(env(safe-area-inset-bottom)+1rem)] lg:pb-0 overflow-y-auto">
        <ul class="menu menu-md gap-1 p-4 flex-1 w-full">
          <li>
            <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/home']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: true}" class="text-base">
              <fa-icon [icon]="faHome"></fa-icon> Home
            </a>
          </li>
          <li>
            <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/products']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
              <fa-icon [icon]="faAssortment"></fa-icon> Products
            </a>
          </li>
          <!-- Always reachable: the page is also where a check is started by
               hand, which is exactly what a household with nothing open wants. -->
          <li>
            <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/quick-check']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
              <fa-icon [icon]="faQuickCheck"></fa-icon> Quick Check
              @if (openQuickChecks() > 0) {
                <span class="badge badge-sm badge-warning ms-auto">{{ openQuickChecks() }}</span>
              }
            </a>
          </li>

          <li class="menu-title">Meals</li>
          <li>
            <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/recipes']" [class.menu-active]="isPathActive('/recipes') || isPathActive('/recipe')" class="text-base">
              <fa-icon [icon]="faRecipes"></fa-icon> Recipes
            </a>
          </li>
          @if (mealPlannerEnabled) {
            <li>
              <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/meal-plan']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
                <fa-icon [icon]="faMealPlanner"></fa-icon> Meal Planner
              </a>
            </li>
          }

          <li class="menu-title">Shopping</li>
          <li>
            <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/shopping']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
              <fa-icon [icon]="faExport"></fa-icon> Shopping
            </a>
          </li>
          <li>
            <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/receipts']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
              <fa-icon [icon]="faReceipt"></fa-icon> Receipts
            </a>
          </li>
        </ul>

        <div class="border-t border-base-300">
          <ul class="menu menu-md gap-1 p-4 w-full">
            <li>
              <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/manage-household']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
                <fa-icon [icon]="faHousehold"></fa-icon> Settings
              </a>
            </li>
            <li>
              <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/profile']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
                <fa-icon [icon]="faUser"></fa-icon> Profile
              </a>
            </li>
            @if (user?.permissions?.includes('admin')) {
              <li>
                <a (click)="closeDrawerOnMobile(drawerToggle)" [routerLink]="['/admin']" routerLinkActive="menu-active" [routerLinkActiveOptions]="{exact: false}" class="text-base">
                  <fa-icon [icon]="faAdmin"></fa-icon> Admin
                </a>
              </li>
            }
            @if (backendVersion) {
              <li class="mt-2">
                <span class="px-4 text-xs text-base-content/60 select-text">
                  Build: {{ backendVersion }}
                </span>
              </li>
            }
          </ul>
        </div>
      </nav>
    }
  </div>
</div>













